/** @fileoverview Composes answer prompts, continuation recovery, and the chat composer. */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, ChevronDown, ListChecks, X } from 'lucide-react';
import type {
  AskQuestionAnswer,
  AskQuestionData,
  FabrikUIMessage,
  Message,
  QueuedChatMessage,
} from '../../types/chat';
import AskQuestionCard from './AskQuestionCard';
import QueuedMessages from './QueuedMessages';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';

function RetryButton({
  onClick,
  attempt = 0,
  maxRetries = 0,
}: {
  onClick: () => void;
  attempt?: number;
  maxRetries?: number;
}) {
  return (
    <Button
      type="button"
      size="sm"
      className="bg-app-action text-app-action-foreground hover:bg-app-action-hover"
      disabled={attempt > 0}
      onClick={onClick}
    >
      {attempt > 0 ? `Retrying (${attempt}/${maxRetries})` : 'Retry'}
    </Button>
  );
}

type ImplementationStep = {
  title: string;
  acceptanceCriteria: string[];
  completed?: boolean;
};

type ChatComposerPanelProps = {
  className: string;
  messages: FabrikUIMessage[];
  savedMessages: Message[];
  busy: boolean;
  loading: boolean;
  error: string;
  quotaExhausted: boolean;
  composer: ReactNode;
  queuedMessages: QueuedChatMessage[];
  /** Rendered between the queued messages and the composer. */
  checklist?: ReactNode;
  resetSignal: number;
  retryAttempt: number;
  maxRetries: number;
  canRetry: boolean;
  hideWorkflowApprovalQuestion?: boolean;
  onRetry: () => void;
  onDismissError: () => void;
  onAnswer: (answers: AskQuestionAnswer[]) => Promise<void>;
  onAnswerError: (error: unknown) => void;
  onSteer: (id: string) => void;
  onRemoveQueued: (id: string) => void;
};

export function ImplementationChecklist({
  className,
  steps,
  busy,
}: {
  className: string;
  steps: ImplementationStep[];
  busy: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const completeCount = steps.filter((step) => step.completed).length;
  const isComplete = steps.length > 0 && completeCount === steps.length;
  const checklistIdentity = JSON.stringify(
    steps.map(({ title, acceptanceCriteria }) => [title, acceptanceCriteria])
  );
  const previousChecklistIdentity = useRef(checklistIdentity);
  const currentIndex = steps.findIndex((step) => !step.completed);
  const isActiveConnector = (index: number) => currentIndex === -1 || index < currentIndex;
  const progressSegmentColor = (index: number) =>
    currentIndex === -1 || index < currentIndex
      ? 'bg-app-brand-strong'
      : index === currentIndex
      ? 'bg-app-brand-subtle'
      : 'bg-app-surface-muted-strong';

  // Only announce completion when this view watched the steps finish, never for an already-complete plan.
  const sawIncomplete = useRef(false);
  if (steps.length > 0 && !isComplete) sawIncomplete.current = true;

  useEffect(() => {
    if (previousChecklistIdentity.current === checklistIdentity) return;
    previousChecklistIdentity.current = checklistIdentity;
    sawIncomplete.current = !isComplete;
    setDismissed(false);
    setExpanded(false);
  }, [checklistIdentity, isComplete]);
  useEffect(() => {
    if (!isComplete) setDismissed(false);
    else setExpanded(false);
  }, [isComplete]);

  if (isComplete && !sawIncomplete.current) return null;

  if (isComplete && !busy) {
    if (dismissed) return null;
    return (
      <div className={className}>
        <div
          className="mb-2 flex min-h-9 items-center gap-2 rounded-lg border border-app-border bg-app-surface-card px-3 py-2 text-xs text-app-text-muted shadow-sm"
          role="status"
          aria-live="polite"
        >
          <Check className="size-4 text-app-brand-strong" aria-hidden="true" />
          <span className="font-medium text-app-text-secondary">Implementation completed</span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="ml-auto text-app-text-muted hover:bg-app-surface-muted hover:text-app-text-secondary"
            aria-label="Hide completed implementation checklist"
            title="Hide completed checklist"
            onClick={() => setDismissed(true)}
          >
            <X className="size-3.5" aria-hidden="true" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="mb-2 rounded-lg border border-app-border bg-app-surface-card shadow-sm">
        <button
          type="button"
          className="flex min-h-9 w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs text-app-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-app-brand-strong"
          aria-expanded={expanded}
          aria-controls="implementation-todo-items"
          onClick={() => setExpanded((value) => !value)}
        >
          <ListChecks className="size-4 text-app-text-muted" aria-hidden="true" />
          <span className="font-medium">Implementation steps</span>
          <span className="ml-auto text-app-text-muted">
            {completeCount}/{steps.length} complete
          </span>
          <ChevronDown
            className={`size-3.5 text-app-text-muted transition-transform ${
              expanded ? 'rotate-180' : ''
            }`}
            aria-hidden="true"
          />
        </button>
        <div
          className="mx-3 mb-2 flex gap-1"
          role="progressbar"
          aria-label="Implementation progress"
          aria-valuemin={0}
          aria-valuemax={steps.length}
          aria-valuenow={completeCount}
          aria-valuetext={`${completeCount} of ${steps.length} complete${
            currentIndex >= 0 ? `; step ${currentIndex + 1} is current` : ''
          }`}
        >
          {steps.map((step, index) => (
            <span
              key={`${index}-${step.title}`}
              className={`h-1.5 min-w-0 flex-1 rounded-full ${progressSegmentColor(index)}`}
              aria-hidden="true"
            />
          ))}
        </div>
        <div
          id="implementation-todo-items"
          hidden={!expanded}
          className="max-h-56 overflow-y-auto border-t border-app-border px-3 py-2.5"
        >
          <ol className="grid">
            {steps.map((step, index) => {
              const current = index === currentIndex;
              return (
                <li
                  key={`${index}-${step.title}`}
                  aria-current={current ? 'step' : undefined}
                  className="relative flex items-start gap-2 py-1.5"
                >
                  {index < steps.length - 1 && (
                    <span
                      className={`absolute left-2.5 top-3.5 -bottom-3.5 w-px ${
                        isActiveConnector(index) ? 'bg-app-brand-strong' : 'bg-app-border'
                      }`}
                      aria-hidden="true"
                    />
                  )}
                  <span className="relative z-10 flex w-5 shrink-0 justify-center">
                    <span
                      className={`flex size-4 items-center justify-center rounded-full border ${
                        step.completed
                          ? 'border-app-brand-strong bg-app-brand-subtle text-app-brand-strong'
                          : current
                          ? 'border-app-brand-subtle-foreground bg-app-brand-subtle text-app-brand-subtle-foreground'
                          : 'border-app-border bg-app-surface-card text-app-text-muted'
                      }`}
                    >
                      {step.completed ? (
                        <Check className="size-3" aria-hidden="true" />
                      ) : current ? (
                        <span className="size-1.5 rounded-full bg-app-brand-subtle-foreground" />
                      ) : null}
                    </span>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
                      <p className="text-xs font-medium text-app-text">
                        {step.title || `Step ${index + 1}`}
                      </p>
                      <span className="text-xs text-app-text-muted">
                        {step.completed ? 'Complete' : current ? 'Current step' : 'Up next'}
                      </span>
                    </div>
                    {current && step.acceptanceCriteria.length > 0 && (
                      <ul className="mt-1 grid gap-0.5 text-xs text-app-text-muted">
                        {step.acceptanceCriteria.map((criterion, criterionIndex) => (
                          <li key={`${criterionIndex}-${criterion}`}>• {criterion}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </div>
  );
}

export default function ChatComposerPanel({
  className,
  messages,
  savedMessages,
  busy,
  loading,
  error,
  quotaExhausted,
  composer,
  queuedMessages,
  checklist,
  resetSignal,
  retryAttempt,
  maxRetries,
  canRetry,
  hideWorkflowApprovalQuestion = false,
  onRetry,
  onDismissError,
  onAnswer,
  onAnswerError,
  onSteer,
  onRemoveQueued,
}: ChatComposerPanelProps) {
  const [submittedQuestions, setSubmittedQuestions] = useState<{
    key: string;
    messageId?: string;
    resetSignal: number;
  }>();
  const submittedQuestionsKey =
    submittedQuestions?.resetSignal === resetSignal ? submittedQuestions.key : undefined;

  const latestAssistantIndex = messages.reduce(
    (latest, message, index) => (message.role === 'assistant' ? index : latest),
    -1
  );
  const latestUserIndex = messages.reduce(
    (latest, message, index) => (message.role === 'user' ? index : latest),
    -1
  );
  const latestAssistantMessage = messages[latestAssistantIndex];
  const latestAssistantIsCurrent = latestAssistantIndex > latestUserIndex;
  const questionById = new Map<string, AskQuestionData>();
  latestAssistantMessage?.parts.forEach((part) => {
    if (part.type === 'data-ask-question') questionById.set(part.data.id, part.data);
  });
  const latestQuestions = [...questionById.values()];
  const pendingQuestions = latestQuestions.filter(
    (question) =>
      question.phase === 'pending' && !(hideWorkflowApprovalQuestion && question.workflowApproval)
  );
  const questionCardKey = JSON.stringify([
    latestAssistantMessage?.id,
    latestQuestions.map((question) => [
      question.id,
      question.groupId,
      question.questionIndex,
      question.answerBatchId,
    ]),
  ]);
  const latestSavedAssistant = savedMessages.find(
    (message) => message.id === latestAssistantMessage?.id
  );
  const retryBatchId = [...latestQuestions]
    .reverse()
    .find((question) => question.answerBatchId !== undefined)?.answerBatchId;
  const retryBatch = latestQuestions.filter(
    (question) =>
      question.phase === 'answered' &&
      (retryBatchId === undefined || question.answerBatchId === retryBatchId)
  );
  const retryAnswers = retryBatch.flatMap((question) =>
    typeof question.answer === 'string'
      ? [{ question_id: question.id, answer: question.answer }]
      : []
  );
  const canRetryAnswers =
    latestAssistantIsCurrent &&
    !busy &&
    !loading &&
    latestSavedAssistant?.message.status === 'waiting_for_answer' &&
    pendingQuestions.length === 0 &&
    latestQuestions.length > 0 &&
    latestQuestions.every((question) => question.phase === 'answered') &&
    retryBatch.length > 0 &&
    retryAnswers.length === retryBatch.length;
  const retryBatchKey = retryAnswers.map((answer) => answer.question_id).join('|');
  const displayError =
    error || (quotaExhausted ? 'Your shared AI token allowance is exhausted.' : '');
  const canRetryQuestionAnswers =
    !quotaExhausted && canRetryAnswers && retryBatchKey !== submittedQuestionsKey;
  const canRetryPrompt = !quotaExhausted && canRetry;
  const pendingQuestionKey = pendingQuestions.map((question) => question.id).join('|');
  const submittedCurrentQuestions =
    Boolean(submittedQuestionsKey) &&
    submittedQuestionsKey === pendingQuestionKey &&
    submittedQuestions?.messageId === latestAssistantMessage?.id;
  const showQuestionCard =
    latestAssistantIsCurrent && pendingQuestions.length > 0 && !submittedCurrentQuestions;
  const canAnswerQuestions = showQuestionCard && !busy && !loading;

  const answerQuestions = async (answers: AskQuestionAnswer[]) => {
    setSubmittedQuestions({
      key: answers.map((answer) => answer.question_id).join('|'),
      messageId: latestAssistantMessage?.id,
      resetSignal,
    });
    try {
      await onAnswer(answers);
    } catch (error) {
      setSubmittedQuestions(undefined);
      throw error;
    }
  };
  const retryAnsweredQuestions = async () => {
    try {
      await answerQuestions(retryAnswers);
    } catch (error) {
      onAnswerError(error);
    }
  };

  return (
    <div className={className}>
      {(displayError || retryAttempt > 0 || canRetryPrompt || canRetryQuestionAnswers) && (
        <Alert className="mb-2 flex flex-col gap-2 border-app-warning-border bg-app-warning-surface text-app-warning sm:flex-row sm:items-center sm:justify-between">
          <AlertDescription className="min-w-0 flex-1 text-xs text-app-warning">
            {displayError ||
              (retryAttempt > 0
                ? 'The response failed. Retrying automatically.'
                : canRetryAnswers
                ? 'The assistant didn’t finish. Retry to continue.'
                : 'The response failed. Please try again.')}
          </AlertDescription>
          {(canRetryPrompt ||
            canRetryQuestionAnswers ||
            (error && !quotaExhausted && retryAttempt === 0)) && (
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {canRetryPrompt ? (
                <RetryButton onClick={onRetry} attempt={retryAttempt} maxRetries={maxRetries} />
              ) : (
                canRetryQuestionAnswers && (
                  <RetryButton onClick={() => void retryAnsweredQuestions()} />
                )
              )}
              {error && !quotaExhausted && retryAttempt === 0 && (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className="text-app-warning"
                  type="button"
                  aria-label="Dismiss error"
                  onClick={onDismissError}
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              )}
            </div>
          )}
        </Alert>
      )}
      <QueuedMessages messages={queuedMessages} onSteer={onSteer} onRemove={onRemoveQueued} />
      {checklist}
      {!showQuestionCard && composer}
      <div hidden={!showQuestionCard}>
        <AskQuestionCard
          key={questionCardKey}
          questions={pendingQuestions}
          disabled={!canAnswerQuestions}
          onAnswer={answerQuestions}
        />
      </div>
    </div>
  );
}
